import { Link, type LinkProps } from '@tanstack/react-router';
import { Flag, Lock, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { Conversation } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Avatar, AvatarFallback } from '@nucleus-commerce/ui/components/avatar';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { initialsOf, shopperName, visitorTag, waitingMinutes } from '../lib/display';

import { SlaBadge } from './sla-badge';

export interface InboxRowProps {
  conversation: Conversation;
  link: LinkProps;
  selected: boolean;
  /** Keyboard cursor (j/k), separate from the open conversation. */
  focused: boolean;
  /** Store name for the badge; omitted on the per-store inbox. */
  storeName?: string | undefined;
  now: number;
}

export function InboxRow({ conversation, link, selected, focused, storeName, now }: InboxRowProps) {
  const { t } = useTranslation('conversations');
  const format = useFormat();
  const name = shopperName(conversation);
  const label =
    name ?? t('row.visitor', { tag: visitorTag(conversation.visitor?.id ?? conversation.id) });
  const unread = conversation.staffUnreadCount > 0;
  const unanswered = waitingMinutes(conversation, now);
  const preview = conversation.lastMessage;
  const ticket = conversation.kind === 'ticket';

  return (
    <Link
      {...link}
      data-conversation-id={conversation.id}
      aria-current={selected ? 'page' : undefined}
      className={cn(
        'hover:bg-accent/60 focus-visible:ring-ring flex gap-3 border-b px-3 py-3 outline-none focus-visible:ring-2 focus-visible:ring-inset',
        selected && 'bg-accent',
        focused && !selected && 'bg-accent/40',
        unanswered !== null && !selected && 'bg-warning/10',
      )}
    >
      <Avatar className="mt-0.5 size-9 shrink-0">
        <AvatarFallback className="text-xs">{name ? initialsOf(name) : '?'}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-baseline gap-2">
          {conversation.priority < 3 ? (
            <Flag
              className={cn(
                'size-3 shrink-0 self-center',
                conversation.priority === 1 ? 'text-destructive' : 'text-warning',
              )}
              aria-label={t(`priority.${conversation.priority}`)}
            />
          ) : null}
          <span className={cn('truncate text-sm', unread ? 'font-semibold' : 'font-medium')}>
            {label}
          </span>
          <time
            className="text-muted-foreground ml-auto shrink-0 text-xs"
            dateTime={conversation.lastMessageAt}
            title={format.utc(conversation.lastMessageAt)}
          >
            {format.relative(conversation.lastMessageAt, new Date(now))}
          </time>
        </div>
        <div className="flex items-center gap-1.5">
          {preview?.isInternal ? (
            <Lock className="text-muted-foreground size-3 shrink-0" aria-hidden />
          ) : null}
          <p
            className={cn('truncate text-xs', unread ? 'text-foreground' : 'text-muted-foreground')}
          >
            {ticket && conversation.subject ? (
              <span className="text-foreground font-medium">
                {conversation.subject}
                {preview ? ' · ' : null}
              </span>
            ) : null}
            {preview ? (
              <>
                {preview.authorType === 'staff' ? t('row.you') : null}
                {preview.preview}
              </>
            ) : ticket && conversation.subject ? null : (
              t(`kind.${conversation.kind}`)
            )}
          </p>
          {unread ? (
            <span
              className="bg-primary ml-auto size-2 shrink-0 rounded-full"
              aria-label={t('row.unread', { count: conversation.staffUnreadCount })}
            />
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {storeName ? (
            <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
              {storeName}
            </Badge>
          ) : null}
          {conversation.kind !== 'chat' ? (
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
              {t(`kind.${conversation.kind}`)}
            </Badge>
          ) : null}
          {ticket ? <SlaBadge conversation={conversation} now={now} /> : null}
          {!ticket && conversation.slaBreached && unanswered !== null ? (
            <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">
              {t('row.needsReply', { minutes: unanswered })}
            </Badge>
          ) : null}
          {conversation.missed ? (
            <Badge variant="outline" className="border-warning h-5 px-1.5 text-[10px]">
              {t('row.missed')}
            </Badge>
          ) : null}
          {conversation.rating !== null ? (
            <span className="text-muted-foreground inline-flex items-center gap-0.5 text-[10px]">
              <Star className="size-3" aria-hidden />
              {t(
                `rating.${conversation.rating > 0 ? 'good' : conversation.rating < 0 ? 'bad' : 'neutral'}`,
              )}
            </span>
          ) : null}
          {conversation.tags.slice(0, 3).map((tag) => (
            <Badge key={tag} variant="secondary" className="h-5 px-1.5 text-[10px] font-normal">
              {tag}
            </Badge>
          ))}
          {conversation.assignee ? (
            <span
              className="text-muted-foreground ml-auto truncate text-[10px]"
              title={conversation.assignee.name}
            >
              {conversation.assignee.name}
            </span>
          ) : null}
        </div>
      </div>
    </Link>
  );
}
